import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Heading, Spinner, Text, VStack } from '@chakra-ui/react'
import { CsatPublicLayout } from '@/components/csat/CsatPublicLayout'
import {
  getCsatApiError,
  getCsatSurveyStatus,
  submitCsatScore,
} from '@/lib/services/csatService'
import { CSAT_RATINGS, normalizeCsatScore, type CsatScore } from '@/types/csat'

type PageState = 'loading' | 'ready' | 'submitting' | 'expired' | 'invalid'

export default function CsatSubmitPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const autoSubmitted = useRef(false)

  const token = searchParams.get('token')?.trim() ?? ''
  const scoreParam = searchParams.get('score') ?? searchParams.get('rating')
  const urlScore = normalizeCsatScore(scoreParam)

  const [pageState, setPageState] = useState<PageState>('loading')
  const [error, setError] = useState<string | null>(null)
  const [selectedScore, setSelectedScore] = useState<CsatScore | null>(urlScore)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const goToThankYou = useCallback(
    (alreadyRated: boolean) => {
      navigate('/csat/thank-you', { replace: true, state: { alreadyRated } })
    },
    [navigate]
  )

  const submitScore = useCallback(
    async (score: CsatScore) => {
      if (!token) return
      setPageState('submitting')
      setIsSubmitting(true)
      setError(null)
      try {
        const res = await submitCsatScore(token, score)
        const alreadyRated = res.message?.toLowerCase().includes('already') ?? false
        goToThankYou(alreadyRated)
      } catch (err: unknown) {
        const { message, status } = getCsatApiError(err)
        if (message.toLowerCase().includes('already')) {
          goToThankYou(true)
          return
        }
        if (status === 410 || message.toLowerCase().includes('expired')) {
          setPageState('expired')
          setError(message)
          return
        }
        setPageState('ready')
        setIsSubmitting(false)
        setError(message)
      }
    },
    [token, goToThankYou]
  )

  // Validate token before showing UI or auto-submitting (avoids race with invalid tokens).
  useEffect(() => {
    if (!token) {
      setPageState('invalid')
      setError('Invalid survey link. Please use the link from your CSAT email.')
      return
    }

    let cancelled = false

    void (async () => {
      try {
        const status = await getCsatSurveyStatus(token)
        if (cancelled) return

        if (status.expired) {
          setPageState('expired')
          setError(status.message)
          return
        }

        if (status.already_rated) {
          goToThankYou(true)
          return
        }

        setPageState('ready')
      } catch (err: unknown) {
        if (cancelled) return
        const { message, status } = getCsatApiError(err)
        if (status === 410) {
          setPageState('expired')
          setError(message)
        } else {
          setPageState('invalid')
          setError(message)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [token, goToThankYou])

  // Email deep-link: auto-submit only after token validation succeeds.
  useEffect(() => {
    if (pageState !== 'ready' || urlScore == null || autoSubmitted.current) {
      return
    }
    autoSubmitted.current = true
    void submitScore(urlScore)
  }, [pageState, urlScore, submitScore])

  if (pageState === 'loading') {
    return (
      <CsatPublicLayout>
        <VStack spacing={4} py={8}>
          <Spinner size="lg" color="blue.500" />
          <Text color="gray.600">Loading survey…</Text>
        </VStack>
      </CsatPublicLayout>
    )
  }

  if (pageState === 'submitting' || (urlScore != null && pageState === 'ready')) {
    return (
      <CsatPublicLayout>
        <VStack spacing={4} py={8}>
          <Spinner size="lg" color="blue.500" />
          <Text color="gray.600">Submitting your feedback…</Text>
          {error && (
            <Text color="red.500" fontSize="sm" textAlign="center">
              {error}
            </Text>
          )}
        </VStack>
      </CsatPublicLayout>
    )
  }

  if (pageState === 'expired') {
    return (
      <CsatPublicLayout>
        <VStack spacing={4} py={8} textAlign="center">
          <Heading size="md">Survey link expired</Heading>
          <Text color="gray.600" fontSize="sm">
            {error ?? 'This survey link is no longer valid.'}
          </Text>
        </VStack>
      </CsatPublicLayout>
    )
  }

  if (pageState === 'invalid') {
    return (
      <CsatPublicLayout>
        <VStack spacing={4} py={8} textAlign="center">
          <Heading size="md">Invalid survey link</Heading>
          <Text color="gray.600" fontSize="sm">
            {error ?? 'This link is not valid. Please use the link from your CSAT email.'}
          </Text>
        </VStack>
      </CsatPublicLayout>
    )
  }

  // Token only (?token=...) — show 4 rating options matching the email template.
  return (
    <CsatPublicLayout>
      <VStack spacing={6} align="stretch">
        <VStack spacing={1} textAlign="center">
          <Heading size="md">How did we do?</Heading>
          <Text color="gray.500" fontSize="sm">
            Please select one option below
          </Text>
        </VStack>

        <VStack spacing={3} w="100%">
          {CSAT_RATINGS.map((rating) => (
            <Button
              key={rating.score}
              colorScheme="blue"
              size="lg"
              w="100%"
              maxW="320px"
              mx="auto"
              onClick={() => {
                setSelectedScore(rating.score)
                void submitScore(rating.score)
              }}
              isLoading={isSubmitting && selectedScore === rating.score}
              isDisabled={isSubmitting}
            >
              {rating.label}
            </Button>
          ))}
        </VStack>

        {error && (
          <Text color="red.500" fontSize="sm" textAlign="center">
            {error}
          </Text>
        )}

        <Text fontSize="xs" color="gray.500" textAlign="center">
          Your selection will be recorded immediately. You only need to rate once.
        </Text>
      </VStack>
    </CsatPublicLayout>
  )
}
